import React, { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import { X, RotateCcw, Check } from 'lucide-react';
import { createStreetMap } from './streetMap.jsx';
import { PROVINCES as CITY_CENTERS } from './provinces.jsx';
import './locationPicker.scss';

// محدوده = چهارضلعی. دو حالت جدا تا لمس و کشیدن با حرکتِ نقشه قاطی نشود:
//   «رسم»: چهار گوشه را یکی‌یکی لمس کنید (نقشه با یک انگشت جابه‌جا نمی‌شود؛ زوم با + و - یا دو انگشت)
//   «ویرایش»: گوشه‌ها را بکشید تا شکل بزرگ/کوچک شود؛ وسط هر ضلع را بکشید تا آن ضلع جابه‌جا شود
// خروجی: Feature (Polygon) در قالب GeoJSON، همان قالبِ قبلی

const PROVINCES = [
  { name: 'تهران', lat: 35.6892, lng: 51.389, zoom: 10 },
  { name: 'اصفهان', lat: 32.6546, lng: 51.668, zoom: 9 },
  { name: 'فارس', lat: 29.5918, lng: 52.5837, zoom: 8 },
  { name: 'خراسان رضوی', lat: 36.297, lng: 59.6062, zoom: 8 },
  { name: 'آذربایجان شرقی', lat: 38.08, lng: 46.2919, zoom: 8 },
  { name: 'آذربایجان غربی', lat: 37.5527, lng: 45.076, zoom: 8 },
  { name: 'البرز', lat: 35.418, lng: 50.978, zoom: 10 },
  { name: 'گیلان', lat: 37.28, lng: 49.58, zoom: 9 },
  { name: 'مازندران', lat: 36.565, lng: 53.05, zoom: 8 },
  { name: 'خوزستان', lat: 31.32, lng: 48.68, zoom: 8 },
  { name: 'کرمان', lat: 30.28, lng: 57.08, zoom: 7 },
  { name: 'یزد', lat: 31.897, lng: 54.367, zoom: 8 },
  { name: 'قم', lat: 34.64, lng: 50.876, zoom: 10 },
  { name: 'همدان', lat: 34.79, lng: 48.51, zoom: 9 },
  { name: 'کردستان', lat: 35.31, lng: 46.99, zoom: 8 },
  { name: 'کرمانشاه', lat: 34.31, lng: 47.06, zoom: 8 },
  { name: 'لرستان', lat: 33.48, lng: 48.35, zoom: 8 },
  { name: 'مرکزی', lat: 34.09, lng: 49.7, zoom: 8 },
  { name: 'قزوین', lat: 36.27, lng: 50.0, zoom: 9 },
  { name: 'زنجان', lat: 36.67, lng: 48.48, zoom: 8 },
  { name: 'سمنان', lat: 35.57, lng: 53.4, zoom: 7 },
  { name: 'گلستان', lat: 36.84, lng: 54.43, zoom: 8 },
  { name: 'اردبیل', lat: 38.25, lng: 48.29, zoom: 8 },
  { name: 'بوشهر', lat: 28.97, lng: 50.84, zoom: 8 },
  { name: 'هرمزگان', lat: 27.19, lng: 56.28, zoom: 7 },
  { name: 'سیستان و بلوچستان', lat: 29.5, lng: 60.86, zoom: 6 },
  { name: 'چهارمحال و بختیاری', lat: 32.33, lng: 50.86, zoom: 8 },
  { name: 'کهگیلویه و بویراحمد', lat: 30.67, lng: 51.59, zoom: 8 },
  { name: 'ایلام', lat: 33.64, lng: 46.42, zoom: 8 },
  { name: 'خراسان جنوبی', lat: 32.86, lng: 59.22, zoom: 7 },
  { name: 'خراسان شمالی', lat: 37.47, lng: 57.33, zoom: 8 },
];

// CITY_CENTERS و PROVINCES این‌جا [lat, lng] هستند؛ MapLibre [lng, lat] می‌خواهد
function cityView(name) {
  const c = CITY_CENTERS.find((p) => p.name === name);
  if (c && c.center) return { center: [c.center[1], c.center[0]], zoom: 12 };
  const p = PROVINCES.find((x) => x.name === name);
  if (p) return { center: [p.lng, p.lat], zoom: Math.max(p.zoom, 11) };
  return { center: [51.389, 35.6892], zoom: 11 };
}

// گوشه‌های یک محدوده‌ی GeoJSON (بدون نقطه‌ی تکراریِ پایانی)
function ringFrom(shape) {
  try {
    const g = shape && (shape.geometry || shape);
    const ring = g.type === 'Polygon' ? g.coordinates[0] : [];
    if (!ring || ring.length < 4) return [];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closed = first[0] === last[0] && first[1] === last[1];
    return (closed ? ring.slice(0, -1) : ring).map(([lng, lat]) => [lng, lat]);
  } catch (e) {
    return [];
  }
}

// دکمه‌های گوشه و وسط ضلع: ساده، مشکی، با حاشیه‌ی سفید
function makeHandle(kind) {
  const el = document.createElement('div');
  const isCorner = kind === 'corner';
  Object.assign(el.style, {
    width: isCorner ? '22px' : '26px',
    height: isCorner ? '22px' : '12px',
    borderRadius: isCorner ? '50%' : '6px',
    background: isCorner ? '#111111' : '#ffffff',
    border: '2px solid #111111',
    boxShadow: '0 2px 6px rgba(0,0,0,.3)',
    cursor: 'grab',
    touchAction: 'none',
    boxSizing: 'border-box',
  });
  return el;
}

function LocationPicker({ onClose, onConfirm, initialProvince, initialShape, confirmLabel = 'تایید محدوده', intro = null }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const loadedRef = useRef(false);
  const ptsRef = useRef([]); // گوشه‌ها: [[lng, lat], …] (۰ تا ۴)
  const modeRef = useRef('draw');
  const cornerMarks = useRef([]);
  const sideMarks = useRef([]);
  const [mode, setMode] = useState('draw');
  const [count, setCount] = useState(0);
  const [selectedProvince, setSelectedProvince] = useState(() =>
    initialProvince && PROVINCES.some((p) => p.name === initialProvince) ? initialProvince : 'تهران'
  );

  // در حالت رسم، جابه‌جا کردنِ نقشه با یک انگشت خاموش است (زوم با دو انگشت و دکمه‌ها آزاد است)
  const setPanEnabled = (on) => {
    const m = mapRef.current;
    if (!m) return;
    if (on) m.dragPan.enable();
    else m.dragPan.disable();
  };

  const updateGeometry = () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const p = ptsRef.current;
    cornerMarks.current.forEach((mk, i) => {
      if (p[i]) mk.setLngLat(p[i]);
    });
    sideMarks.current.forEach((mk, i) => {
      if (p.length === 4) {
        const j = (i + 1) % 4;
        mk.setLngLat([(p[i][0] + p[j][0]) / 2, (p[i][1] + p[j][1]) / 2]);
      }
    });
    const features = [];
    if (p.length >= 2) {
      const line = p.length === 4 ? [...p, p[0]] : p;
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } });
    }
    if (p.length === 4) {
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[...p, p[0]]] } });
    }
    map.getSource('draw').setData({ type: 'FeatureCollection', features });
  };

  const rebuildMarkers = () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    cornerMarks.current.forEach((m) => m.remove());
    sideMarks.current.forEach((m) => m.remove());
    cornerMarks.current = [];
    sideMarks.current = [];
    const p = ptsRef.current;
    const editable = modeRef.current === 'edit';

    p.forEach((c, i) => {
      const mk = new maplibregl.Marker({ element: makeHandle('corner'), draggable: editable }).setLngLat(c).addTo(map);
      mk.on('drag', () => {
        const ll = mk.getLngLat();
        ptsRef.current[i] = [ll.lng, ll.lat];
        updateGeometry();
      });
      cornerMarks.current.push(mk);
    });

    if (editable) {
      for (let i = 0; i < 4; i++) {
        const j = (i + 1) % 4;
        const mid = [(p[i][0] + p[j][0]) / 2, (p[i][1] + p[j][1]) / 2];
        const mk = new maplibregl.Marker({ element: makeHandle('side'), draggable: true }).setLngLat(mid).addTo(map);
        mk.on('drag', () => {
          const ll = mk.getLngLat();
          const pts = ptsRef.current;
          const a = pts[i];
          const b = pts[j];
          const oldMid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          const dx = ll.lng - oldMid[0];
          const dy = ll.lat - oldMid[1];
          pts[i] = [a[0] + dx, a[1] + dy];
          pts[j] = [b[0] + dx, b[1] + dy];
          updateGeometry();
        });
        sideMarks.current.push(mk);
      }
    }
  };

  const applyMode = (next) => {
    modeRef.current = next;
    setMode(next);
    setPanEnabled(next === 'draw' ? false : true);
    rebuildMarkers();
  };

  useEffect(() => {
    const map = createStreetMap(mapContainerRef.current, cityView(selectedProvince));
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('draw', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'draw-fill',
        type: 'fill',
        source: 'draw',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': '#111111', 'fill-opacity': 0.1 },
      });
      map.addLayer({
        id: 'draw-line',
        type: 'line',
        source: 'draw',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#111111', 'line-width': 3 },
      });
      loadedRef.current = true;

      const shape = ringFrom(initialShape);
      if (shape.length === 4) {
        ptsRef.current = shape;
        setCount(4);
        applyMode('edit');
        const lngs = shape.map((x) => x[0]);
        const lats = shape.map((x) => x[1]);
        map.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { padding: 40, animate: false }
        );
      } else {
        applyMode('draw');
      }
      updateGeometry();
    });

    // حالت رسم: هر لمس یک گوشه؛ با گوشه‌ی چهارم خودکار به حالت ویرایش می‌رود
    map.on('click', (e) => {
      if (modeRef.current !== 'draw') return;
      if (ptsRef.current.length >= 4) return;
      ptsRef.current = [...ptsRef.current, [e.lngLat.lng, e.lngLat.lat]];
      setCount(ptsRef.current.length);
      if (ptsRef.current.length === 4) {
        applyMode('edit');
      } else {
        rebuildMarkers();
      }
      updateGeometry();
    });

    return () => {
      loadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProvinceChange = (e) => {
    const name = e.target.value;
    setSelectedProvince(name);
    if (mapRef.current) {
      const v = cityView(name);
      mapRef.current.flyTo({ center: v.center, zoom: v.zoom });
    }
  };

  // شروع از اول
  const handleReset = () => {
    ptsRef.current = [];
    setCount(0);
    applyMode('draw');
    updateGeometry();
  };

  const canConfirm = count === 0 || count === 4;

  const handleConfirm = () => {
    const p = ptsRef.current;
    if (p.length !== 4) {
      if (p.length === 0) {
        onConfirm?.(null);
        onClose?.();
      }
      return;
    }
    onConfirm?.({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [[...p, p[0]]] },
    });
    onClose?.();
  };

  const hint =
    mode === 'draw'
      ? `گوشه‌های محدوده را یکی‌یکی لمس کنید (${count} از ۴)`
      : 'گوشه‌ها را بکشید تا شکل بزرگ یا کوچک شود، و وسط هر ضلع را بکشید تا جابه‌جا شود';

  return (
    <div className="location-picker">
      <div className="location-picker__header">
        <button type="button" className="location-picker__close" onClick={onClose} aria-label="بستن">
          <X size={20} strokeWidth={1.8} />
        </button>

        <select className="location-picker__province-select" value={selectedProvince} onChange={handleProvinceChange}>
          {PROVINCES.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>

        <button type="button" className="location-picker__clear" onClick={handleReset} disabled={count === 0} aria-label="شروع دوباره">
          <RotateCcw size={18} strokeWidth={1.8} />
        </button>
      </div>

      {intro && (
        <p style={{ margin: 0, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.7, background: '#fff8e1', color: '#5d4a00', fontFamily: 'Vazirmatn, sans-serif' }}>
          {intro}
        </p>
      )}
      <div className="location-picker__map" ref={mapContainerRef} />

      <div className="location-picker__footer">
        <p className="location-picker__hint">{hint}</p>
        <button type="button" className="location-picker__confirm" onClick={handleConfirm} disabled={!canConfirm}>
          <Check size={16} strokeWidth={2.2} />
          <span>{confirmLabel}</span>
        </button>
      </div>
    </div>
  );
}

export default LocationPicker;
